/**
 * Macro Capture — Records CmdInterpreter-dispatched actions into reusable
 * workflow DAGs with parameterization and replay support.
 *
 * Subscribes to EventBroker action events to capture actionId and parameters
 * for each dispatched action. Captured actions are assembled into a Workflow
 * DAG via createFromSequence(), with support for parameterizing concrete
 * values as named variables.
 *
 * @module gibbs/workflow/macroCapture
 */

import { EventBroker } from '../../eventbroker';
import { Workflow } from './Workflow';
import { executeWorkflow } from './executor';
import { saveWorkflow, loadWorkflow } from './workflowLibrary';
import { getAllActions, getAction } from '../registry';

/**
 * Best-effort capture of a manual action's parameters. UserAction.do() carries
 * positional args (e.g. loadProteinFromPdb('4RMG')); map them onto the
 * ActionDef's ordered inputs+parameters by position, keeping only serializable
 * primitives (string/number/boolean). Object args (compounds, atoms) are
 * skipped — they can't be stored in a saved macro. Without this, a recorded
 * load-protein step has no pdbId and can't replay, which broke the macro.
 *
 * Exception: a positional arg may be a rich DOMAIN object rather than a bare
 * value — loading a protein through the browser dispatches ChooseProtein with a
 * MapCase, not a pdbId string. When the target param is `pdbId` we pull the id
 * off that object (MapCase uses capital-D `pdbID`) so a captured protein-load
 * step still carries a replayable pdbId. Without this the load step captures an
 * empty pdbId and fails on replay with "pdbId could not be resolved".
 */
function extractPrimitiveParams(actionId, args) {
    if (!Array.isArray(args) || args.length === 0) return {};
    const def = getAction(actionId);
    if (!def) return {};
    const paramDefs = [...(def.inputs || []), ...(def.parameters || [])];
    const out = {};
    for (let i = 0; i < paramDefs.length && i < args.length; i++) {
        const name = paramDefs[i].name;
        const v = args[i];
        const t = typeof v;
        if (t === 'string' || t === 'number' || t === 'boolean') {
            out[name] = v;
        } else if (v && typeof v === 'object' && name === 'pdbId') {
            const pdb = v.pdbID ?? v.pdbId;
            if (typeof pdb === 'string' && pdb) out[name] = pdb;
        }
    }
    return out;
}

// Lazily-built map of UserCmd name -> registry actionId. Manual UI actions
// dispatch through UserActions carrying a cmd NAME (e.g. 'EnergyMinimize'),
// not a registry actionId, so we reverse-map via each ActionDef's `userAction`
// field. All registry-backed actions are recordable — including view /
// interaction-highlight / dipole toggles, which are exactly the kind of
// visualization setup a user wants to save as a macro.
//
// The macro denylist below filters *recording noise* only. It is NOT a claim
// that these actions can't be composed — every implemented action is composable
// (availability is expressed via preconditions, not exclusion). Two distinct
// kinds of noise, kept in separate sets because the distinction is real:

// (1) Incidental navigation / selection + panel chrome — transient view/cursor
//     moves and panel open/close that aren't deliberate workflow steps.
const MACRO_EXCLUDE_NAV = new Set([
    'reset-view', 'recenter-view', 'select', 'select-atom', 'next-compound',
    'open-protein-browser', 'open-side-panel', 'close-side-panel',
]);

// (2) Auto-fired load machinery — internal fetches/refreshes/auto-computes that
//     fire *automatically* during a protein load or view change, not as
//     deliberate user steps. Capturing them polluted (and broke) recorded
//     macros.
//     NOTE: the *deliberate* `load-protein-from-pdb` is intentionally NOT here.
//     It is a capturable + parameterizable step so a saved macro can re-ask
//     which protein on replay (see the protein-picker plan). The map/file load
//     variants stay excluded: they are workspace-restore paths, not the "run
//     this macro on a new PDB" story.
const MACRO_EXCLUDE_AUTOFIRE = new Set([
    'load-protein-from-map', 'load-protein-from-file',
    'get-protein-pdb', 'refresh-fragment-info', 'refresh-all-fragments',
    'fetch-prolif-data', 'fetch-maps', 'fetch-connection-maps',
    'get-gife-energies', 'predict-admet',
]);

/**
 * Whether an action is filtered from macro capture as recording noise.
 * Exported for testing the denylist reconciliation. NOTE: exclusion here means
 * "don't auto-record this noise," NOT "this action isn't composable."
 * @param {string} actionId
 * @returns {boolean}
 */
export function isMacroExcluded(actionId) {
    return MACRO_EXCLUDE_NAV.has(actionId) || MACRO_EXCLUDE_AUTOFIRE.has(actionId);
}

// Some UserAction names map to more than one registry action — the three
// load-protein variants all dispatch through `ChooseProtein`. For macro capture
// we bind such a name to a single canonical actionId so the reverse-map can't
// depend on registry iteration order, and so a future un-denylist of the
// map/file variants can't silently steal the mapping.
const CANONICAL_USERACTION = new Map([
    ['ChooseProtein', 'load-protein-from-pdb'],
]);

let userActionMap = null;
function resolveActionId(userActionName) {
    if (!userActionName) return null;
    if (!userActionMap) {
        userActionMap = {};
        for (const a of getAllActions()) {
            if (a.userAction && !isMacroExcluded(a.id)) {
                userActionMap[a.userAction] = a.id;
            }
        }
        // Canonical overrides win regardless of iteration order (as long as the
        // canonical target is itself capturable).
        for (const [name, id] of CANONICAL_USERACTION) {
            if (!isMacroExcluded(id)) userActionMap[name] = id;
        }
    }
    return userActionMap[userActionName] || null;
}

// ─── Module State ─────────────────────────────────────────────────────────────

/** @type {boolean} */
let recording = false;

/** @type {Array<{actionId: string, params: Object}>} */
let capturedActions = [];

/** @type {Function|null} - Stored handler reference for unsubscribe */
let eventHandler = null;

// ─── Event Names ──────────────────────────────────────────────────────────────

/**
 * The EventBroker event name published when an action completes.
 * CmdInterpreter publishes this with { actionId, params, result }.
 * If this event does not exist in the current EventBroker, the macro
 * capture also supports a callback hook via `onActionDispatched()`.
 */
const ACTION_EVENT = 'actionCompleted';

// ─── Callback Hook ────────────────────────────────────────────────────────────

/** @type {Array<Function>} */
const hooks = [];

/**
 * Register a callback that macro capture calls internally.
 * External code (e.g., CmdInterpreter post-dispatch) can call
 * `notifyActionDispatched(actionId, params)` to feed the recorder.
 *
 * @param {Function} fn - Callback receiving (actionId, params)
 * @returns {Function} The registered callback (for later removal)
 */
export function onActionDispatched(fn) {
    hooks.push(fn);
    return fn;
}

/**
 * Remove a previously registered callback.
 * @param {Function} fn
 */
export function offActionDispatched(fn) {
    const idx = hooks.indexOf(fn);
    if (idx >= 0) hooks.splice(idx, 1);
}

/**
 * Notify macro capture that an action was dispatched.
 * Call this from CmdInterpreter or any post-dispatch hook.
 *
 * @param {string} actionId
 * @param {Object} params
 */
export function notifyActionDispatched(actionId, params = {}) {
    for (const hook of hooks) {
        try { hook(actionId, params); } catch (_) { /* swallow */ }
    }
}

// ─── Recording API ────────────────────────────────────────────────────────────

/**
 * Start recording actions. Subscribes to EventBroker for action events
 * and registers an internal capture hook.
 *
 * @returns {void}
 * @throws {Error} If already recording
 */
export function startRecording() {
    if (recording) {
        throw new Error('Macro capture is already recording. Call stopRecording() first.');
    }

    recording = true;
    capturedActions = [];

    // Internal capture function
    const capture = (actionId, params) => {
        if (!recording) return;
        capturedActions.push({ actionId, params: { ...params } });
    };

    // Subscribe to EventBroker
    eventHandler = EventBroker.subscribe(ACTION_EVENT, (event, data) => {
        if (!data) return;
        // Direct actionId (Gibbs/hook) carries named params — keep them.
        if (data.actionId) {
            capture(data.actionId, data.params || {});
            return;
        }
        // A cmd name (manual UI action via UserAction.do) carries positional
        // args — map the primitive ones onto the action's params so the step
        // can actually replay (e.g. load-protein keeps its pdbId).
        const actionId = resolveActionId(data.userActionName);
        if (actionId) capture(actionId, extractPrimitiveParams(actionId, data.params));
    });

    // Also register as a callback hook for direct notification
    onActionDispatched(capture);

    // Store capture ref on handler for cleanup
    eventHandler.captureHook = capture;
}

/**
 * Stop recording and build a Workflow DAG from the captured actions.
 *
 * @returns {Object} { workflow, capturedActions }
 * @throws {Error} If not currently recording
 * @throws {Error} If no actions were captured
 */
export function stopRecording() {
    if (!recording) {
        throw new Error('Macro capture is not recording. Call startRecording() first.');
    }

    recording = false;

    // Unsubscribe from EventBroker
    if (eventHandler) {
        EventBroker.unsubscribe(ACTION_EVENT, eventHandler);
        // Remove the capture hook
        if (eventHandler.captureHook) {
            offActionDispatched(eventHandler.captureHook);
        }
        eventHandler = null;
    }

    const captured = [...capturedActions];
    capturedActions = [];

    if (captured.length === 0) {
        throw new Error('No actions were captured during recording.');
    }

    // Build the macro as an ORDERED LIST of nodes, NOT a data-flow pipeline.
    // Recorded actions (view toggles, highlights, dock, minimize, …) don't chain
    // output→input, so createFromSequence's type-compatible-edge requirement
    // would throw ("No type-compatible pairing"). A macro just replays its
    // actions in capture order against the ambient workspace, so we create the
    // nodes with no edges — the executor runs edgeless nodes in creation order
    // and each resolves its inputs from the current workspace.
    const actionIds = captured.map((a) => a.actionId);
    const workflow = new Workflow();
    actionIds.forEach((id) => workflow.addNode(id));

    // Apply captured parameters as paramOverrides on each node
    const nodes = workflow.getNodes();
    for (let i = 0; i < nodes.length; i++) {
        if (captured[i] && captured[i].params) {
            Object.assign(nodes[i].paramOverrides, captured[i].params);
        }
    }

    return { workflow, capturedActions: captured };
}

/**
 * Whether macro capture is currently recording.
 * @returns {boolean}
 */
export function isRecording() {
    return recording;
}

// ─── Parameterization ─────────────────────────────────────────────────────────

/**
 * Replace a concrete parameter value with a named variable marker.
 *
 * The variable marker is stored as:
 * ```json
 * { "type": "variable", "name": "targetProtein", "defaultValue": "4YZU" }
 * ```
 *
 * @param {import('./Workflow.js').Workflow} workflow - Workflow to modify
 * @param {string} nodeId - Node ID containing the parameter
 * @param {string} paramName - Parameter name to parameterize
 * @param {string} variableName - Human-readable variable name
 * @returns {void}
 * @throws {Error} If the node or parameter is not found
 */
export function parameterize(workflow, nodeId, paramName, variableName) {
    const node = workflow.getNode(nodeId);
    if (!node) {
        throw new Error(`Node "${nodeId}" not found in workflow.`);
    }

    const currentValue = node.paramOverrides[paramName];
    if (currentValue === undefined) {
        throw new Error(
            `Parameter "${paramName}" not found in node "${nodeId}" paramOverrides.`
        );
    }

    // Don't double-parameterize
    if (currentValue && typeof currentValue === 'object' && currentValue.type === 'variable') {
        throw new Error(
            `Parameter "${paramName}" in node "${nodeId}" is already parameterized `
            + `as variable "${currentValue.name}".`
        );
    }

    node.paramOverrides[paramName] = {
        type: 'variable',
        name: variableName,
        defaultValue: currentValue,
    };
}

// ─── Target-protein contract (protein-picker) ───────────────────────────────────

/** The registry action that a deliberate PDB load records as. */
const LOAD_PROTEIN_ACTION = 'load-protein-from-pdb';

/**
 * Derive the macro's target-protein binding so replay can re-ask which protein
 * (see the protein-picker plan). ONE variable name — `targetProtein` — bound
 * two ways, chosen automatically by whether the macro captured a deliberate
 * load step:
 *
 * - Path A ('in-dag'): a captured `load-protein-from-pdb` step. Its `pdbId` is
 *   parameterized as `targetProtein` (default = recorded PDB); the macro is
 *   self-contained — replay substitutes the picked value and the macro loads it.
 * - Path B ('metadata'): no load step (macro built on a pre-loaded workspace).
 *   Uses the caller-supplied `activePdbId`; replay loads the picked protein
 *   out-of-band, then replays the load-free macro.
 * - null: neither a load step nor an active protein — a protein-agnostic macro
 *   (no re-ask; runs on whatever is loaded).
 *
 * Mutates `workflow` only in Path A (to add the variable). Idempotent: an
 * already-parameterized load step is returned as-is, never double-parameterized.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {Object} [opts]
 * @param {string} [opts.activePdbId] currently-loaded protein, for Path B
 * @param {string} [opts.variableName='targetProtein']
 * @returns {?{mode:'in-dag'|'metadata', variableName:string, defaultValue:string, nodeId?:string}}
 */
export function deriveTargetProtein(workflow, opts = {}) {
    const { activePdbId, variableName = 'targetProtein' } = opts;

    const loadNode = workflow.getNodes().find((n) => n.actionId === LOAD_PROTEIN_ACTION);
    if (loadNode) {
        const current = loadNode.paramOverrides.pdbId;
        if (current && typeof current === 'object' && current.type === 'variable') {
            return {
                mode: 'in-dag',
                nodeId: loadNode.id,
                variableName: current.name,
                defaultValue: current.defaultValue,
            };
        }
        if (current != null && current !== '') {
            parameterize(workflow, loadNode.id, 'pdbId', variableName);
            return { mode: 'in-dag', nodeId: loadNode.id, variableName, defaultValue: current };
        }
    }

    if (activePdbId) {
        return { mode: 'metadata', variableName, defaultValue: activePdbId };
    }

    return null;
}

/**
 * Read a saved macro's target-protein binding for replay — the read-only
 * inverse of {@link deriveTargetProtein}. Prefers an in-DAG `targetProtein`
 * variable (Path A); falls back to the macro's `targetProtein` metadata
 * (Path B). Returns null for a protein-agnostic macro (replay with no re-ask).
 *
 * The caller (WorkflowPane) uses `mode` to decide how to bind the picked value:
 * - 'in-dag'   → `replayMacro(id, { [variableName]: picked }, …)`
 * - 'metadata' → load `picked` via UserActions.ChooseProtein, then
 *                `replayMacro(id, {}, …)`
 *
 * @param {import('./Workflow.js').Workflow} workflow - loaded macro workflow
 * @param {Object} [metadata] - loaded macro metadata (from loadWorkflow)
 * @param {string} [variableName='targetProtein']
 * @returns {?{mode:'in-dag'|'metadata', variableName:string, defaultValue:string}}
 */
export function resolveMacroTarget(workflow, metadata = {}, variableName = 'targetProtein') {
    const v = getVariables(workflow).find((x) => x.variableName === variableName);
    if (v) {
        return { mode: 'in-dag', variableName, defaultValue: v.defaultValue };
    }
    if (metadata && metadata.targetProtein) {
        return { mode: 'metadata', variableName, defaultValue: metadata.targetProtein };
    }
    return null;
}

// ─── Save Macro ───────────────────────────────────────────────────────────────

/**
 * Save a parameterized workflow to the workflow library as a macro.
 *
 * @param {import('./Workflow.js').Workflow} workflow - The (optionally parameterized) workflow
 * @param {string} name - Macro name
 * @param {string} [description] - Optional description
 * @param {string} [proteinName] - Optional protein name
 * @param {string} [targetProtein] - Default target protein for a Path-B macro
 *   (load NOT in the DAG). For Path-A macros the default lives in the load
 *   step's `targetProtein` variable, so this may be left empty. See the
 *   protein-picker plan.
 * @returns {string} The saved workflow ID
 */
export function saveMacro(workflow, name, description, proteinName, targetProtein) {
    return saveWorkflow(workflow, {
        name,
        description: description || '',
        proteinName: proteinName || '',
        targetProtein: targetProtein || '',
        isMacro: true,
    });
}

// ─── Replay Macro ─────────────────────────────────────────────────────────────

/**
 * Load a macro from the library, substitute variable values, and execute.
 *
 * @param {string} id - Workflow library ID of the macro
 * @param {Object} variableValues - Values for parameterized variables:
 *   `{ variableName: value, ... }`
 * @param {Object} workspace - Workspace state for execution
 * @param {Object} [callbacks] - Execution callbacks (see executeWorkflow)
 * @returns {Promise<Object>} { completed, provenance, errors }
 * @throws {Error} If the macro is not found
 */
export async function replayMacro(id, variableValues = {}, workspace, callbacks = {}) {
    const loaded = loadWorkflow(id);
    if (!loaded) {
        throw new Error(`Macro "${id}" not found in workflow library.`);
    }

    const { workflow } = loaded;

    // Substitute variable markers with provided values
    substituteVariables(workflow, variableValues);

    return executeWorkflow(workflow, workspace, callbacks);
}

/**
 * Walk all nodes in a workflow and replace variable markers with
 * provided values (or their default values if not provided).
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @param {Object} variableValues - `{ variableName: value }`
 */
function substituteVariables(workflow, variableValues) {
    for (const node of workflow.getNodes()) {
        for (const [paramName, value] of Object.entries(node.paramOverrides)) {
            if (value && typeof value === 'object' && value.type === 'variable') {
                const resolvedValue = variableValues[value.name] !== undefined
                    ? variableValues[value.name]
                    : value.defaultValue;
                node.paramOverrides[paramName] = resolvedValue;
            }
        }
    }
}

/**
 * Extract all variable markers from a workflow for UI display.
 *
 * @param {import('./Workflow.js').Workflow} workflow
 * @returns {Array<{nodeId: string, paramName: string, variableName: string, defaultValue: *}>}
 */
export function getVariables(workflow) {
    const variables = [];
    for (const node of workflow.getNodes()) {
        for (const [paramName, value] of Object.entries(node.paramOverrides)) {
            if (value && typeof value === 'object' && value.type === 'variable') {
                variables.push({
                    nodeId: node.id,
                    paramName,
                    variableName: value.name,
                    defaultValue: value.defaultValue,
                });
            }
        }
    }
    return variables;
}
